import React, { useEffect, useState } from 'react'
import { Form, InputGroup } from 'react-bootstrap'

interface MonetaryInputProps {
  value: string // Format: "CURRENCYCODEVALUE" (e.g., "RSD1000.00")
  onChange: (value: string) => void
  defaultCurrency?: string
  disabled?: boolean
  size?: 'sm' | 'lg'
}

export const MonetaryInput: React.FC<MonetaryInputProps> = ({ value, onChange, defaultCurrency = 'RSD', disabled = false, size = 'sm' }) => {
  // Parse value: "RSD1000.00" -> currency: "RSD", amount: "1000.00"
  const parseValue = (val: string): { currency: string; amount: string } => {
    if (!val || val.length < 3) {
      return { currency: defaultCurrency, amount: '' }
    }

    // Currency code is first 3 characters
    const currency = val.substring(0, 3)
    const amount = val.substring(3)

    return { currency: currency.toUpperCase(), amount }
  }

  const { currency: initialCurrency, amount: initialAmount } = parseValue(value)
  const [currency, setCurrency] = useState<string>(initialCurrency || defaultCurrency)
  const [amount, setAmount] = useState<string>(initialAmount || '')

  useEffect(() => {
    const parsed = parseValue(value)
    setCurrency(parsed.currency || defaultCurrency)
    setAmount(parsed.amount || '')
  }, [value, defaultCurrency])

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = e.target.value
    setAmount(newAmount)
    // Pass raw value during typing - no formatting to avoid focus loss
    onChange(currency + newAmount)
  }

  const parseAmountForDisplay = (val: string): number => {
    if (!val) return 0
    // European format (167.000,89): comma = decimal, dot = thousands
    if (val.includes(',')) {
      const normalized = val.replace(/\./g, '').replace(',', '.')
      const num = parseFloat(normalized)
      return isNaN(num) ? 0 : num
    }
    return parseFloat(val) || 0
  }

  const handleAmountBlur = () => {
    if (amount) {
      const parsed = parseAmountForDisplay(amount)
      const formatted = parsed.toFixed(2)
      setAmount(formatted)
      onChange(currency + formatted)
    } else if (amount === '0') {
      const formatted = '0.00'
      setAmount(formatted)
      onChange(currency + formatted)
    }
  }

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCurrency = e.target.value.toUpperCase().substring(0, 3)
    setCurrency(newCurrency)
    onChange(newCurrency + amount)
  }

  return (
    <InputGroup size={size}>
      <InputGroup.Text className="fw-bold bg-light">
        {new Intl.NumberFormat('sr-RS', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
        }).format(parseAmountForDisplay(amount))}
      </InputGroup.Text>
      <Form.Control
        type="text"
        value={currency}
        onChange={handleCurrencyChange}
        maxLength={3}
        className="text-muted"
        style={{ maxWidth: '60px' }}
        disabled={disabled}
        placeholder="RSD"
      />
      <Form.Control
        type="text"
        inputMode="decimal"
        value={amount}
        onChange={handleAmountChange}
        onBlur={handleAmountBlur}
        className="text-muted"
        disabled={disabled}
        placeholder="0.00"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.stopPropagation()
          }
        }}
      />
    </InputGroup>
  )
}
